import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { fetchProducts } from "../lib/products";
import { useSiteContent } from "../lib/siteContent";
import { Lines, FadeUp } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import Footer from "../components/Footer";

const DEPARTMENTS = ["Men", "Women", "Kids"];
const CATEGORIES = ["T-Shirts", "Hoodies", "Jackets", "Bottoms", "Accessories"];
const DEFAULT_COLLECTIONS = ["Drop 001", "Snake Edition", "Raised Reality", "Essentials"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const COLORS = ["Onyx", "Bone", "Ash", "Concrete"];
const SORTS = [
    ["featured", "Featured"],
    ["price-asc", "Price: Low to High"],
    ["price-desc", "Price: High to Low"],
    ["name", "Alphabetical"],
];

function FilterGroup({ title, options, value, onChange, testid }) {
    return (
        <div className="border-b border-[rgba(10,10,10,0.12)] py-6">
            <p className="mb-4 text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">{title}</p>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => (
                    <button
                        key={o}
                        data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => onChange(value === o ? null : o)}
                        className={`rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                            value === o
                                ? "border-[#0A0A0A] bg-[#0A0A0A] text-[#F2F0EA] shadow-[0_8px_20px_-6px_rgba(10,10,10,0.35)]"
                                : "border-[rgba(10,10,10,0.15)] text-[#8E8D8A] hover:-translate-y-0.5 hover:border-[#0A0A0A] hover:text-[#0A0A0A] hover:shadow-[0_10px_24px_-8px_rgba(10,10,10,0.25)]"
                        }`}
                    >
                        {o}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function ShopPage() {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);
    const { collections: managedCollections } = useSiteContent();
    const COLLECTIONS = managedCollections.length ? managedCollections.map((c) => c.name) : DEFAULT_COLLECTIONS;

    const department = params.get("department");
    const category = params.get("category");
    const collection = params.get("collection");
    const size = params.get("size");
    const color = params.get("color");
    const sort = params.get("sort") || "featured";

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        setParams(next, { replace: true });
    };

    useEffect(() => {
        setLoading(true);
        const query = {};
        if (department) query.department = department;
        if (category) query.category = category;
        if (collection) query.collection = collection;
        if (size) query.size = size;
        if (color) query.color = color;
        if (sort) query.sort = sort;
        fetchProducts(query)
            .then((data) => setProducts(data))
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    }, [department, category, collection, size, color, sort]);

    const heading = department || collection || category || "Shop All";
    const activeCount = [department, category, collection, size, color].filter(Boolean).length;

    return (
        <div data-testid="shop-page" className="pt-32">
            <header className="px-5 sm:px-8 lg:px-16">
                <p className="mb-3 text-[9px] uppercase tracking-[0.4em] text-[#6B6A66]">Vantage Owners Club</p>
                <Lines
                    lines={[heading]}
                    className="font-display text-4xl font-black uppercase tracking-tighter text-[#0A0A0A] sm:text-6xl lg:text-7xl"
                />
                <div className="mt-10 flex items-center justify-between border-y border-[rgba(10,10,10,0.12)] py-4">
                    <button
                        data-testid="filter-open-button"
                        onClick={() => setDrawerOpen(true)}
                        className="flex items-center gap-3 font-display text-[10px] font-bold uppercase tracking-[0.3em] text-[#0A0A0A]"
                    >
                        <SlidersHorizontal size={14} strokeWidth={1.5} />
                        Filter {activeCount > 0 && <span className="text-[#8E8D8A]">({activeCount})</span>}
                    </button>
                    <p className="hidden text-[9px] uppercase tracking-[0.3em] text-[#6B6A66] sm:block">
                        {products.length} {products.length === 1 ? "Piece" : "Pieces"}
                    </p>
                    <div className="relative">
                        <button
                            data-testid="sort-toggle-button"
                            onClick={() => setSortOpen((v) => !v)}
                            className="flex items-center gap-2 font-display text-[10px] font-bold uppercase tracking-[0.3em] text-[#0A0A0A]"
                        >
                            Sort <ChevronDown size={13} className={`transition-transform duration-300 ${sortOpen ? "rotate-180" : ""}`} />
                        </button>
                        <AnimatePresence>
                            {sortOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                    className="absolute right-0 top-full z-20 mt-3 w-56 border border-[rgba(10,10,10,0.12)] bg-[#ECEAE4]"
                                >
                                    {SORTS.map(([val, label]) => (
                                        <button
                                            key={val}
                                            data-testid={`sort-option-${val}`}
                                            onClick={() => {
                                                setParam("sort", val === "featured" ? null : val);
                                                setSortOpen(false);
                                            }}
                                            className={`block w-full px-5 py-3 text-left text-[10px] uppercase tracking-[0.2em] transition-colors ${
                                                sort === val ? "text-[#0A0A0A]" : "text-[#8E8D8A] hover:text-[#0A0A0A]"
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-[1600px] px-5 py-16 sm:px-8 lg:px-16">
                {loading ? (
                    <div className="grid grid-cols-2 gap-6 lg:grid-cols-4 lg:gap-8">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="aspect-[4/5] animate-pulse bg-[#ECEAE4]" />
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <p data-testid="shop-no-results" className="py-24 text-center text-xs uppercase tracking-[0.3em] text-[#6B6A66]">
                        No pieces match these filters
                    </p>
                ) : (
                    <motion.div layout className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4 lg:gap-8">
                        <AnimatePresence mode="popLayout">
                            {products.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    layout
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.97 }}
                                    transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <ProductCard product={p} priority={i < 4} />
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>

            <AnimatePresence>
                {drawerOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setDrawerOpen(false)}
                            className="fixed inset-0 z-[160] bg-[rgba(10,10,10,0.75)]"
                        />
                        <motion.aside
                            data-testid="filter-drawer"
                            initial={{ x: "-100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "-100%" }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            className="fixed left-0 top-0 z-[170] h-full w-full max-w-sm overflow-y-auto border-r border-[rgba(10,10,10,0.12)] bg-[#F2F0EA] px-8 py-6"
                        >
                            <div className="flex items-center justify-between">
                                <h2 className="font-display text-xs font-bold uppercase tracking-[0.35em]">Filter</h2>
                                <button data-testid="filter-close-button" onClick={() => setDrawerOpen(false)} aria-label="Close filters">
                                    <X size={18} strokeWidth={1.5} />
                                </button>
                            </div>
                            <FilterGroup title="Department" options={DEPARTMENTS} value={department} onChange={(v) => setParam("department", v)} testid="filter-department" />
                            <FilterGroup title="Category" options={CATEGORIES} value={category} onChange={(v) => setParam("category", v)} testid="filter-category" />
                            <FilterGroup title="Size" options={SIZES} value={size} onChange={(v) => setParam("size", v)} testid="filter-size" />
                            <FilterGroup title="Collection" options={COLLECTIONS} value={collection} onChange={(v) => setParam("collection", v)} testid="filter-collection" />
                            <FilterGroup title="Color" options={COLORS} value={color} onChange={(v) => setParam("color", v)} testid="filter-color" />
                            <button
                                data-testid="filter-clear-button"
                                onClick={() => setParams({}, { replace: true })}
                                className="link-underline mt-8 text-[10px] uppercase tracking-[0.3em] text-[#8E8D8A] transition-colors hover:text-[#0A0A0A]"
                            >
                                Clear All
                            </button>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
            <Footer />
        </div>
    );
}
